import React, { Fragment } from 'react';
import { Formik } from 'formik';
import Yup from 'yup';
import _ from 'lodash';

import Button, { BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import TickerUploader from 'buyside/components/presentation/TickerUploader/tickerUploader';
import WatchlistTickerAutocompleteContainer from 'buyside/components/container/WatchlistTickerAutocomplete/WatchlistTickerAutocompleteContainer';
import OnboardingTickerSuggestionsContainer from 'buyside/components/container/Onboarding/OnboardingTickerSuggestionsContainer';
import OnboardingTickerSuggestionCardContainer from 'buyside/components/container/Onboarding/OnboardingTickerSuggestionCardContainer';

import { isEmpty } from 'buyside/util/lodashAlt';

import styles from 'buyside/components/presentation/Onboarding/OnboardingForm/onboardingFormStyles';

const onboardingTickersForm = ({ watchlist, handleSubmit }) => {
  const {
    massImports,
    geographies,
    sectors,
    entitiesFromMassImport,
    entitiesRegularFollows
  } = watchlist;
  const combinedEntityFollows = entitiesFromMassImport.concat(entitiesRegularFollows);
  const filterList = combinedEntityFollows.map(entity => entity.id);
  const filterFunction = suggestion => suggestion.entityId;

  // Adds some prioriety for certain sectors and geographies
  let activeSector, activeGeography;
  if (!isEmpty(sectors)) {
    activeSector =
      _.find(sectors, sector => sector.name === 'Technology') ||
      _.find(sectors, sector => sector.name === 'Financials') ||
      sectors[0];
  }
  if (!isEmpty(geographies)) {
    activeGeography =
      _.find(geographies, geography => geography.name === 'North America') || geographies[0];
  }

  return (
    <div className={styles.onboardingMainArea}>
      <h2 className={styles.onboardingFormHeader}>Watchlisted Tickers</h2>
      <div className={styles.onboardingFormInfo}>
        Add at least 5 companies/tickers you’re interested in tracking. Your watched
        companies/tickers are not displayed publicly. This information is private.
      </div>
      <div className={`${styles.formFieldArea} ${styles.smallWidth}`}>
        <h3 className={`${styles.fieldHeader} ${styles.noBorder}`}>Upload Tickers as a CSV</h3>
        <TickerUploader />
        {!isEmpty(massImports) && (
          <Fragment>
            <h3 className={`${styles.fieldHeader} ${styles.noBorder}`}>Uploaded files</h3>
            {massImports.map((massImport, idx) => (
              <div className={styles.uploadedFile} key={idx}>
                {massImport.fileName} ({massImport.numberOfTickers})
              </div>
            ))}
          </Fragment>
        )}
      </div>
      <div className={`${styles.formFieldArea} ${styles.smallWidth}`}>
        <h3 className={`${styles.fieldHeader} ${styles.noBorder}`}>Search Tickers</h3>
        <div className={styles.autocomplete}>
          <WatchlistTickerAutocompleteContainer />
        </div>
      </div>
      <div className={styles.formArea}>
        <OnboardingTickerSuggestionsContainer
          cardComponent={OnboardingTickerSuggestionCardContainer}
          title="Popular Tickers"
          categoryName="all"
          categoryType="all"
          filterList={filterList}
          filterFunction={filterFunction}
        />
        {activeSector && (
          <OnboardingTickerSuggestionsContainer
            cardComponent={OnboardingTickerSuggestionCardContainer}
            title={`Popular ${activeSector.name} Tickers`}
            categoryName={activeSector.name}
            categoryType="sector"
            filterList={filterList}
            filterFunction={filterFunction}
          />
        )}
        {activeGeography && (
          <OnboardingTickerSuggestionsContainer
            cardComponent={OnboardingTickerSuggestionCardContainer}
            title={`Popular ${activeGeography.name} Tickers`}
            categoryName={activeGeography.name}
            categoryType="geography"
            filterList={filterList}
            filterFunction={filterFunction}
          />
        )}
      </div>
      <Formik onSubmit={handleSubmit} validationSchema={Yup.object()}>
        {props => <_onboardingTickersFormikForm {...props} watchlist={watchlist} />}
      </Formik>
    </div>
  );
};

const _onboardingTickersFormikForm = props => {
  const { handleSubmit, watchlist, isSubmitting } = props;
  const { entitiesFromMassImport, entitiesRegularFollows } = watchlist;
  const hasTickers = entitiesRegularFollows.length + entitiesFromMassImport.length >= 5;
  return (
    <form onSubmit={handleSubmit}>
      <div className={styles.nextSectionNav}>
        <Button
          text="Next Section - Build Your Community"
          type={BUTTON_TYPES.PRIMARY}
          isLoading={isSubmitting}
          disabled={!hasTickers}
          id="NextButton"
        />
        {!hasTickers && (
          <div className={styles.validationText}>Select at least 5 tickers to continue.</div>
        )}
      </div>
    </form>
  );
};

export default onboardingTickersForm;
